/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, ElementRef, EventEmitter, Output} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';

@Component({
    selector: 'hole-criteria-editor',
    template: `
        <!-- Show as modal -->
        @if (!inlineMode) {
          <p-dialog [(visible)]="isModalOpen" [modal]="true" (onHide)="onDialogHide()"
                  (onShow)="onDialogShown()"
                  [focusOnShow]="false"
            [closable]="true" [dismissableMask]="true" header="Hole Criteria Editor" class="custom-wide-dialog">
            <ng-container *ngTemplateOutlet="editorContent"></ng-container>
            <ng-template pTemplate="footer">
                <div class="hole-criteria-dialog-footer">
                    <button pButton type="button" label="Apply" icon="pi pi-check"
                            (click)="onApplyClick()" title="Save changes and close"></button>
                    <button pButton type="button" label="Cancel" icon="pi pi-times" severity="secondary"
                            (click)="onCancelClick()" title="Discard changes and close"></button>
                </div>
            </ng-template>
          </p-dialog>
        }

        <!-- Show inline -->
        @if (inlineMode) {
          <div class="inline-editor">
            <ng-container *ngTemplateOutlet="editorContent"></ng-container>
          </div>
        }

        <!-- Shared template -->
        <ng-template #editorContent>
          <p-table class="component-edit-table" [value]="tableData" [tableStyle]="{'width': '100%'}">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 30px !important; min-width: 30px !important; max-width: 30px !important;"></th>
                <th>Label</th>
                <th colspan="3" style="text-align: center">Hole Size (mm)</th>
                <th>Hole Feature Number</th>
                <th>Connector Part Numbers</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
              <tr>
                <td style="width: 30px !important; min-width: 30px !important; max-width: 30px !important;">
                  <button pButton severity="danger" class="p-button-text" tabindex="-1"
                  (click)="onDeleteRow(rowIndex)" title="Delete Row"><i class="pi pi-trash" pButtonIcon></i></button>
                </td>
                @for (col of row; track trackByColIndex(colIndex, col); let colIndex = $index) {
                  <td [ngClass]="'col' + colIndex">
                    <input
                      [id]="'input-C' + colIndex + 'R' + rowIndex"
                      type="text"
                      class="p-inputtext p-component"
                      [ngStyle]="{ 'text-align': colIndex === 1 ? 'end' : 'start'}"
                      [(ngModel)]="tableData[rowIndex][colIndex]"
                      (keydown)="onKeydown($event, rowIndex, colIndex)"
                      (blur)="onCellBlur(rowIndex, colIndex, $event)"
                      />
                    </td>
                    <!-- Insert a full cell *after* column 1 -->
                    @if (colIndex === 1) {
                      <td style="width: 8px; min-width:unset; text-align: center;">x</td>
                    }
                  }
                </tr>
              </ng-template>
            </p-table>
            <br/>
            <button pButton tabindex="-1" (click)="onAddRow()" title="Add Row"><i class="pi pi-plus" pButtonIcon></i><span pButtonLabel>Add Row</span></button>
          </ng-template>
        `,
    styles: [`
      .close-btn {
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
      }
      .col0        {width: 100px;}
      .col1, .col2 {width: 50px;}
      .col3        {width: 150px;}
      .col4        {width: 250px;}
      button {
        border: none;
      }
      table {
        border-collapse: collapse;
        width: 100%;
      }
      tbody {word-break: break-word;}
      td {padding: 0px !important;}
      th {font-weight: 600 !important;}
      th, td {min-width: 50px;}
      input {
        width: 100%;
        border: none;
        outline: none;
      }
      .hole-criteria-dialog-footer {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 0.5rem;
        width: 100%;
      }
    `],
    standalone: false
})
export class HoleCriteriaEditorComponent implements ICellEditorAngularComp {
    private params: any;
    tableData: string[][] = [];
    isModalOpen = false;
    inlineMode = false;
    /** When the modal closes, true = commit (Apply), false = discard (Cancel, X, mask, Escape). */
    private commitOnDialogClose = false;
    // Call this.valueChange.emit(newValue) whenever user input changes
    @Output() valueChange = new EventEmitter<any>();

    constructor(
        private elementRef: ElementRef
    ) {}

    agInit(params: any): void {
        this.params = params;
        this.inlineMode = params.inlineMode ?? false;
        this.parseValue(params.value);
        if (!this.inlineMode) {
            this.isModalOpen = true;
        }
    }

    parseValue(value: string): void {
        this.tableData = [];
        for (const rowValue of value?.split('|') || []) {
            const row = [];
            let colIndex = 0;
            for (const column of rowValue?.split('\\') || []) {
                if (colIndex++ === 1) {
                    column.split('x').forEach((cell) => {
                        row.push(cell);
                    });
                } else {
                    row.push(column);
                }
            }
            this.tableData.push(row);
        }
        if (this.tableData.length === 0) {
            this.tableData.push(['', '', '', '', '']);
        }
    }

    afterGuiAttached(): void {
        this.isModalOpen = true;
        // Inline: focus first cell. Modal: onDialogShown runs after p-dialog opens (focusOnShow is false).
        if (this.inlineMode) {
            this.setFocus(0, 0);
        }
    }

    /** After modal is visible; avoids PrimeNG moving focus to the first focusable (delete button). */
    onDialogShown(): void {
        this.setFocus(0, 0, 50);
    }

    onCellBlur(rowIndex: number, colIndex: number, event: Event): void {
        this.valueChange.emit(this.tableData[rowIndex][colIndex]);
    }

    trackByColIndex(index: number, item: any): number {
        return index;
    }

    onApplyClick(): void {
        this.commitOnDialogClose = true;
        this.isModalOpen = false;
    }

    onCancelClick(): void {
        this.commitOnDialogClose = false;
        this.isModalOpen = false;
    }

    /**
     * Single place to finish ag-Grid editing after the dialog is hidden (Apply, Cancel, X, mask, Escape).
     */
    onDialogHide(): void {
        if (this.inlineMode) {
            return;
        }
        const discard = !this.commitOnDialogClose;
        this.commitOnDialogClose = false;
        this.params?.api?.stopEditing(discard); // Ensures ag-Grid knows the editor is closed
    }

    getValue(): any {
        return this.tableData.map(row => {
            let result = '';
            for (let i = 0; i < row.length; i++) {
                result += row[i];
                if (i < row.length - 1) {
                    result += (i === 1) ? 'x' : '\\';
                }
            }
            return result;
        }).filter(o => o !== '\\x\\\\').join('|');
    }

    onDeleteRow(rowIndex: number): void {
        this.tableData.splice(rowIndex, 1);
    }

    onAddRow(): void {
        this.tableData.push(['', '', '', '', '']);
    }

    isPopup(): boolean {
        return !this.inlineMode;
    }

    setFocus(rowIndex: number, colIndex: number, delayMs = 0) {
        if (rowIndex < this.tableData.length && colIndex < this.tableData[rowIndex].length) {
            const run = () => {
                const nextInput = this.elementRef.nativeElement.querySelector(
                    `#input-C${colIndex}R${rowIndex}`
                ) as HTMLInputElement | null;
                nextInput?.focus();
                nextInput?.select();
            };
            if (delayMs > 0) {
                setTimeout(run, delayMs);
            } else {
                setTimeout(run, 0);
            }
        }
    }

    onKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number) {
        if (event.key === 'Enter') {
            this.onEnterKey(event, rowIndex, colIndex);
        } else if (event.key === 'Tab') {
            const isShift = event.shiftKey;
            this.onTabKey(event, rowIndex, colIndex, isShift);
        }
    }

    onEnterKey(event: Event, rowIndex: number, colIndex: number) {
        event.preventDefault();
        event.stopPropagation();
        const nextRow = rowIndex + 1;
        if (nextRow >= this.tableData.length) {
            this.onAddRow();
        }
        this.setFocus(nextRow, 0);
    }
    onTabKey(event: Event, rowIndex: number, colIndex: number, shift: boolean) {
        event.preventDefault();
        event.stopPropagation();
        let nextCol = (colIndex + 1) % 5;
        let nextRow = rowIndex + ((colIndex === 4) ? 1 : 0);
        if (shift) {
            nextCol = (colIndex + 4) % 5;
            nextRow = rowIndex + ((colIndex === 0) ? -1 : 0);
            if (nextRow === -1) {
                nextRow = nextCol = 0;
            }
        }
        this.setFocus(nextRow, nextCol);
    }

}
